import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, RotateCcw, Globe, AlertTriangle, DollarSign, Eye, Network, Compass,
  ChevronRight, Lock, Send, Loader2, CheckCircle2, ArrowLeft,
  Server, Shield, Blocks, Wrench, Scale,
  TrendingDown, FileSearch, Calculator, PieChart, BarChart3, FileText
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { useAuth } from '@/context/AuthContext';
import { COUNTRIES, REGIONS, SOLUTIONS } from '@/data/coverageData';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════
   PERSONA CONFIGURATIONS
   ═══════════════════════════════════════════ */

const PERSONAS = {
  tax: {
    id: 'tax',
    title: 'Tax Director',
    subtitle: 'Compliance & Regulatory',
    description: 'Assess your compliance health across filing risk, cost pressure, visibility, vendor fragmentation, and regulatory readiness.',
    color: '#0066CC',
    accent: 'taxera-blue',
    icon: Compass,
    intro: {
      badge: 'Tax Compliance Diagnostic',
      headline: ['How healthy is your', 'compliance function?'],
      body: '3 minutes. 5 honest answers. One score that tells you where you stand — and where you\'re exposed.',
    },
    painDimensions: [
      {
        id: 'filingRisk', label: 'Filing Risk',
        question: 'Missed deadlines or rejected filings in the last 12 months?',
        icon: AlertTriangle, accent: '#ef4444', accentBg: 'bg-red-50',
        options: [
          { label: 'Clean record', score: 20, tone: 'good' },
          { label: 'Occasional issues', score: 13, tone: 'warn' },
          { label: 'Regular problems', score: 6, tone: 'bad' },
          { label: 'No visibility', score: 0, tone: 'critical' },
        ],
        insights: [
          'Strong compliance discipline — rare at your scale.',
          'Even occasional misses create audit exposure and late-filing penalties.',
          'Regular filing issues signal systemic gaps — not people problems.',
          'If you don\'t know, assume it\'s happening. Lack of visibility is the risk.',
        ],
      },
      {
        id: 'costPressure', label: 'Cost Pressure',
        question: 'How would you describe your compliance spending trend?',
        icon: DollarSign, accent: '#f97316', accentBg: 'bg-orange-50',
        options: [
          { label: 'Stable & efficient', score: 20, tone: 'good' },
          { label: 'Growing slowly', score: 13, tone: 'warn' },
          { label: 'Accelerating fast', score: 6, tone: 'bad' },
          { label: 'Out of control', score: 0, tone: 'critical' },
        ],
        insights: [
          'That\'s unusual — worth protecting.',
          'Slow growth compounds. In 3 years this is a different number.',
          'Accelerating spend with no additional coverage is a red flag.',
          'You\'re not alone — but you can\'t budget-plan around chaos.',
        ],
      },
      {
        id: 'visibility', label: 'Visibility',
        question: 'Can you see your global compliance status in real-time?',
        icon: Eye, accent: '#8b5cf6', accentBg: 'bg-violet-50',
        options: [
          { label: 'Full visibility', score: 20, tone: 'good' },
          { label: 'Partial view', score: 13, tone: 'warn' },
          { label: 'Mostly blind', score: 6, tone: 'bad' },
          { label: 'No dashboard', score: 0, tone: 'critical' },
        ],
        insights: [
          'Rare advantage — most multinationals can\'t say this.',
          'Partial visibility is dangerous — it creates false confidence.',
          'If you can\'t see it, you can\'t manage it. Gaps hide in the dark.',
          'You\'re managing compliance on faith. That\'s not a strategy.',
        ],
      },
      {
        id: 'fragmentation', label: 'Vendor Landscape',
        question: 'How many external providers handle your tax compliance?',
        icon: Network, accent: '#0ea5e9', accentBg: 'bg-sky-50',
        options: [
          { label: '1-3 vendors', score: 20, tone: 'good' },
          { label: '4-7 vendors', score: 13, tone: 'warn' },
          { label: '8-15 vendors', score: 6, tone: 'bad' },
          { label: '15+ / lost count', score: 0, tone: 'critical' },
        ],
        insights: [
          'Lean and manageable. Well done.',
          'That\'s 4-7 contracts, integrations, and SLAs to manage.',
          'Your team spends more time managing vendors than managing tax.',
          'That\'s not a compliance function. It\'s a procurement function.',
        ],
      },
      {
        id: 'readiness', label: 'Future Readiness',
        question: 'Prepared for upcoming mandates? (ViDA, Pillar 2, CTC)',
        icon: Compass, accent: '#14b8a6', accentBg: 'bg-teal-50',
        options: [
          { label: 'Fully prepared', score: 20, tone: 'good' },
          { label: 'Planning now', score: 13, tone: 'warn' },
          { label: 'Aware, not started', score: 6, tone: 'bad' },
          { label: 'What mandates?', score: 0, tone: 'critical' },
        ],
        insights: [
          'Ahead of 90% of the market. Maintain the lead.',
          'Good — but timelines are aggressive. Planning must become execution.',
          'The clock is ticking. Early movers get 12-18 months advantage.',
          'This answer alone justifies this diagnostic. You have gaps you can\'t see.',
        ],
      },
    ],
    transformMetrics: (totalScore, countryCount) => {
      const scoreInfo = getScoreLabel(totalScore);
      const projectedScore = Math.min(totalScore + 35, 96);
      const projectedInfo = getScoreLabel(projectedScore);
      return [
        { label: 'Compliance vendors', before: `${Math.round(countryCount * 0.8)}`, after: '1', beforeColor: '#ef4444', afterColor: '#10b981' },
        { label: 'Health score', before: `${totalScore}`, after: `${projectedScore}`, beforeColor: scoreInfo.color, afterColor: projectedInfo.color },
        { label: 'Mandate coverage', before: 'Partial', after: 'Full', beforeColor: '#f97316', afterColor: '#10b981' },
        { label: 'Regulatory readiness', before: 'Reactive', after: 'Proactive', beforeColor: '#ef4444', afterColor: '#10b981' },
      ];
    },
    reportTitle: 'Compliance Diagnostic Report',
    reportDesc: 'Detailed country-mandate breakdown, risk analysis, and transformation roadmap.',
    costModel: (answers, countryCount, mandateCount) => {
      const s = (id) => {
        const d = PERSONAS.tax.painDimensions.find(d => d.id === id);
        return d?.options[answers[id]]?.score ?? 10;
      };
      const vendorCost = countryCount * (25000 - s('fragmentation') * 700);
      const penaltyCost = countryCount * (15000 - s('filingRisk') * 750);
      const manualLabor = countryCount * (8000 - s('visibility') * 300);
      const regulatoryCost = mandateCount * (12000 - s('readiness') * 500);
      const total = vendorCost + penaltyCost + manualLabor + regulatoryCost;
      return {
        headline: 'What is your global tax compliance really costing you?',
        subline: `Based on ${countryCount} countries and ${mandateCount} mandate types, here's the estimated annual cost of operating at your current maturity.`,
        lineItems: [
          { label: 'External vendor management', amount: vendorCost, note: `${countryCount} countries × multi-vendor overhead` },
          { label: 'Filing penalty exposure', amount: penaltyCost, note: 'Late filings, rejections, corrections' },
          { label: 'Manual compliance labor', amount: manualLabor, note: 'Status tracking, data gathering, reporting' },
          { label: 'Regulatory change management', amount: regulatoryCost, note: `${mandateCount} mandate types × reactive adaptation` },
        ],
        total,
        taxeraTotal: Math.round(total * 0.38),
        savingsNote: 'Single platform, one integration, proactive regulatory updates',
      };
    },
  },

  it: {
    id: 'it',
    title: 'IT Director',
    subtitle: 'Architecture & Integration',
    description: 'Evaluate your tax technology stack — integration complexity, security posture, scalability, and technical debt hiding in your compliance infrastructure.',
    color: '#14b8a6',
    accent: 'taxera-teal',
    icon: Server,
    intro: {
      badge: 'IT Architecture Assessment',
      headline: ['How sustainable is your', 'tax technology stack?'],
      body: '3 minutes. 5 honest answers. Discover where integration debt, security gaps, and scalability limits are hiding in your compliance infrastructure.',
    },
    painDimensions: [
      {
        id: 'integrationComplexity', label: 'Integration Complexity',
        question: 'How many separate interfaces connect your ERP to tax compliance tools?',
        icon: Blocks, accent: '#f97316', accentBg: 'bg-orange-50',
        options: [
          { label: '1-2 interfaces', score: 20, tone: 'good' },
          { label: '3-5 interfaces', score: 13, tone: 'warn' },
          { label: '6-10 interfaces', score: 6, tone: 'bad' },
          { label: '10+ / no idea', score: 0, tone: 'critical' },
        ],
        insights: [
          'Clean and manageable — you have a real integration strategy.',
          'Each interface is a maintenance liability. 3-5 is the tipping point before complexity compounds.',
          'Six or more interfaces means your IT team is spending more time on tax compliance plumbing than innovation.',
          'If you don\'t know the number, the number is too high. That\'s integration debt compounding silently.',
        ],
      },
      {
        id: 'dataSecurity', label: 'Data Security',
        question: 'How confident are you in the security of tax data flows between systems?',
        icon: Shield, accent: '#ef4444', accentBg: 'bg-red-50',
        options: [
          { label: 'Fully audited', score: 20, tone: 'good' },
          { label: 'Mostly secure', score: 13, tone: 'warn' },
          { label: 'Gaps exist', score: 6, tone: 'bad' },
          { label: 'No audit done', score: 0, tone: 'critical' },
        ],
        insights: [
          'That\'s the right level of diligence — maintain the audit cadence.',
          '"Mostly" is a word auditors love to hear. It tells them where to dig.',
          'Known gaps are manageable. The real risk is in the gaps you haven\'t found.',
          'Tax data flows carry sensitive financial data. No audit means no assurance.',
        ],
      },
      {
        id: 'scalability', label: 'Scalability',
        question: 'When a new country mandate hits, what does onboarding look like?',
        icon: TrendingDown, accent: '#8b5cf6', accentBg: 'bg-violet-50',
        options: [
          { label: 'Config change', score: 20, tone: 'good' },
          { label: 'Small project', score: 13, tone: 'warn' },
          { label: 'Major IT project', score: 6, tone: 'bad' },
          { label: 'Custom dev', score: 0, tone: 'critical' },
        ],
        insights: [
          'Configuration-driven onboarding — that\'s platform-grade architecture.',
          'If every mandate is a project, your TCO grows linearly with regulation.',
          'Major IT projects for compliance mean your stack wasn\'t built for change.',
          'Custom development for each mandate is the most expensive way to stay compliant.',
        ],
      },
      {
        id: 'architecture', label: 'ERP Connection',
        question: 'How is your ERP connected to tax compliance systems?',
        icon: Server, accent: '#0ea5e9', accentBg: 'bg-sky-50',
        options: [
          { label: 'Native middleware', score: 20, tone: 'good' },
          { label: 'API connectors', score: 13, tone: 'warn' },
          { label: 'File exports', score: 6, tone: 'bad' },
          { label: 'Manual transfer', score: 0, tone: 'critical' },
        ],
        insights: [
          'Native middleware is the gold standard — two-way, monitored, embedded.',
          'APIs are better than files, but you\'re still building and maintaining the interface.',
          'File-based integration means latency, version control issues, and no real-time visibility.',
          'Manual data transfer is an audit finding waiting to happen. Every hand-off is a risk.',
        ],
      },
      {
        id: 'techDebt', label: 'Technical Debt',
        question: 'How much unplanned maintenance goes to tax compliance systems annually?',
        icon: Wrench, accent: '#14b8a6', accentBg: 'bg-teal-50',
        options: [
          { label: '< 5% of IT budget', score: 20, tone: 'good' },
          { label: '5-15% of IT budget', score: 13, tone: 'warn' },
          { label: '15-30% of IT budget', score: 6, tone: 'bad' },
          { label: '30%+ / unknown', score: 0, tone: 'critical' },
        ],
        insights: [
          'Low maintenance overhead — sign of a well-architected stack.',
          'That\'s significant. Tax compliance shouldn\'t consume 5-15% of your IT capacity.',
          'Nearly a third of your IT budget on unplanned tax work. That\'s not sustainable.',
          'If you don\'t know, it\'s almost certainly higher than you\'d want. Measure it.',
        ],
      },
    ],
    transformMetrics: (totalScore, countryCount) => {
      const scoreInfo = getScoreLabel(totalScore);
      const projectedScore = Math.min(totalScore + 35, 96);
      const projectedInfo = getScoreLabel(projectedScore);
      return [
        { label: 'Integration interfaces', before: `${Math.max(Math.round(countryCount * 0.6), 3)}`, after: '1', beforeColor: '#ef4444', afterColor: '#10b981' },
        { label: 'Architecture score', before: `${totalScore}`, after: `${projectedScore}`, beforeColor: scoreInfo.color, afterColor: projectedInfo.color },
        { label: 'New mandate onboarding', before: 'IT project', after: 'Config', beforeColor: '#f97316', afterColor: '#10b981' },
        { label: 'Tech debt trend', before: 'Growing', after: 'Eliminated', beforeColor: '#ef4444', afterColor: '#10b981' },
      ];
    },
    reportTitle: 'IT Architecture Assessment Report',
    reportDesc: 'Integration analysis, security review, scalability assessment, and middleware migration roadmap.',
    costModel: (answers, countryCount, mandateCount) => {
      const s = (id) => {
        const d = PERSONAS.it.painDimensions.find(d => d.id === id);
        return d?.options[answers[id]]?.score ?? 10;
      };
      const integrationMaint = countryCount * (22000 - s('architecture') * 900);
      const mandateOnboarding = mandateCount * (80000 - s('scalability') * 3500);
      const securityCost = 120000 - s('dataSecurity') * 5000;
      const techDebtCost = 250000 - s('techDebt') * 11000;
      const total = integrationMaint + mandateOnboarding + securityCost + techDebtCost;
      return {
        headline: 'What is your tax technology stack really costing IT?',
        subline: `Across ${countryCount} countries and ${mandateCount} mandate types, this is the estimated annual burden on your IT organization.`,
        lineItems: [
          { label: 'Integration maintenance', amount: integrationMaint, note: `${countryCount} countries × interface upkeep` },
          { label: 'Mandate onboarding projects', amount: mandateOnboarding, note: `${mandateCount} mandate types × project cost` },
          { label: 'Security & audit remediation', amount: securityCost, note: 'Tax data flow security posture' },
          { label: 'Technical debt accumulation', amount: techDebtCost, note: 'Unplanned maintenance & workarounds' },
        ],
        total,
        taxeraTotal: Math.round(total * 0.32),
        savingsNote: 'One native middleware integration, config-based onboarding, built-in security',
      };
    },
  },

  finance: {
    id: 'finance',
    title: 'CFO / Finance',
    subtitle: 'Cost, Accuracy & ROI',
    description: 'Quantify the true cost of your compliance operations — total cost of ownership, reconciliation burden, audit exposure, and return on technology investment.',
    color: '#8b5cf6',
    accent: 'violet',
    icon: BarChart3,
    intro: {
      badge: 'Finance & ROI Assessment',
      headline: ['What is compliance', 'really costing you?'],
      body: '3 minutes. 5 honest answers. Understand the true financial impact of your compliance operations — and where the ROI is hiding.',
    },
    painDimensions: [
      {
        id: 'tco', label: 'Total Cost of Ownership',
        question: 'How has your compliance spending trended over the last 3 years?',
        icon: DollarSign, accent: '#ef4444', accentBg: 'bg-red-50',
        options: [
          { label: 'Declining', score: 20, tone: 'good' },
          { label: 'Flat', score: 13, tone: 'warn' },
          { label: 'Growing 10-20%/yr', score: 6, tone: 'bad' },
          { label: 'Growing 20%+/yr', score: 0, tone: 'critical' },
        ],
        insights: [
          'Declining cost with growing scope — that\'s technology leverage working.',
          'Flat is fine if mandates aren\'t growing. They are. So flat is falling behind.',
          '10-20% annual growth means your costs are outpacing your revenue growth.',
          'That trajectory is unsustainable. You\'re paying more for the same or less coverage.',
        ],
      },
      {
        id: 'accuracy', label: 'Financial Accuracy',
        question: 'How confident are you in the accuracy of your tax reporting data?',
        icon: FileSearch, accent: '#f97316', accentBg: 'bg-orange-50',
        options: [
          { label: 'Fully confident', score: 20, tone: 'good' },
          { label: 'Mostly accurate', score: 13, tone: 'warn' },
          { label: 'Errors found quarterly', score: 6, tone: 'bad' },
          { label: 'Regular corrections', score: 0, tone: 'critical' },
        ],
        insights: [
          'Full confidence means full control. That\'s the goal.',
          '"Mostly" accurate means you\'re correcting after the fact. That costs time and credibility.',
          'Quarterly errors aren\'t a data problem — they\'re a process problem.',
          'Regular corrections mean your tax position is never truly known. That\'s a CFO\'s nightmare.',
        ],
      },
      {
        id: 'reconEffort', label: 'Reconciliation Effort',
        question: 'How much time does your team spend on tax-related reconciliation monthly?',
        icon: Calculator, accent: '#8b5cf6', accentBg: 'bg-violet-50',
        options: [
          { label: '< 2 days', score: 20, tone: 'good' },
          { label: '3-5 days', score: 13, tone: 'warn' },
          { label: '1-2 weeks', score: 6, tone: 'bad' },
          { label: '2+ weeks', score: 0, tone: 'critical' },
        ],
        insights: [
          'Sub-2 days is exceptional efficiency. Your processes are strong.',
          '3-5 days monthly is typical but expensive. That\'s 60 days per year on reconciliation alone.',
          '1-2 weeks per month means reconciliation is consuming your team\'s capacity.',
          'Half the month on reconciliation? Your team can\'t do strategic work. They\'re trapped.',
        ],
      },
      {
        id: 'auditReadiness', label: 'Audit Readiness',
        question: 'If a tax authority requested an audit tomorrow, how ready are you?',
        icon: Scale, accent: '#0ea5e9', accentBg: 'bg-sky-50',
        options: [
          { label: 'Fully prepared', score: 20, tone: 'good' },
          { label: 'Need 1-2 weeks', score: 13, tone: 'warn' },
          { label: 'Need 1-2 months', score: 6, tone: 'bad' },
          { label: 'Not ready', score: 0, tone: 'critical' },
        ],
        insights: [
          'Audit-ready on demand. That\'s the level of control every CFO wants.',
          '1-2 weeks to prepare means your data isn\'t audit-ready by default.',
          '1-2 months means you\'re assembling data, not just presenting it. Big difference.',
          'Audit unreadiness is a financial risk, a reputational risk, and a personal risk.',
        ],
      },
      {
        id: 'roiVisibility', label: 'ROI Visibility',
        question: 'Can you quantify the return on your compliance technology investment?',
        icon: PieChart, accent: '#14b8a6', accentBg: 'bg-teal-50',
        options: [
          { label: 'Clear ROI model', score: 20, tone: 'good' },
          { label: 'Rough estimate', score: 13, tone: 'warn' },
          { label: 'Can\'t quantify', score: 6, tone: 'bad' },
          { label: 'Never measured', score: 0, tone: 'critical' },
        ],
        insights: [
          'Clear ROI visibility is rare and valuable. Use it to justify further investment.',
          'A rough estimate isn\'t enough for the board. Precision builds confidence.',
          'If you can\'t quantify ROI, you can\'t justify investment. Or challenge it.',
          'Unmeasured technology spend is a blind spot. You may be overpaying — or underinvesting.',
        ],
      },
    ],
    transformMetrics: (totalScore, countryCount) => {
      const scoreInfo = getScoreLabel(totalScore);
      const projectedScore = Math.min(totalScore + 35, 96);
      const projectedInfo = getScoreLabel(projectedScore);
      return [
        { label: 'Annual cost trend', before: '+15%/yr', after: '-20%/yr', beforeColor: '#ef4444', afterColor: '#10b981' },
        { label: 'Finance score', before: `${totalScore}`, after: `${projectedScore}`, beforeColor: scoreInfo.color, afterColor: projectedInfo.color },
        { label: 'Reconciliation time', before: 'Weeks', after: 'Hours', beforeColor: '#f97316', afterColor: '#10b981' },
        { label: 'Audit readiness', before: 'Months', after: 'Instant', beforeColor: '#ef4444', afterColor: '#10b981' },
      ];
    },
    reportTitle: 'Finance & ROI Assessment Report',
    reportDesc: 'TCO analysis, reconciliation optimization model, audit readiness plan, and ROI projection.',
    costModel: (answers, countryCount, mandateCount) => {
      const s = (id) => {
        const d = PERSONAS.finance.painDimensions.find(d => d.id === id);
        return d?.options[answers[id]]?.score ?? 10;
      };
      const complianceSpend = countryCount * (28000 - s('tco') * 800);
      const reconLabor = 200000 - s('reconEffort') * 8750;
      const auditPrep = 150000 - s('auditReadiness') * 6750;
      const errorCorrection = 120000 - s('accuracy') * 5500;
      const total = complianceSpend + reconLabor + auditPrep + errorCorrection;
      return {
        headline: 'What is compliance really costing the business?',
        subline: `Across ${countryCount} countries, this is the estimated all-in annual cost of your current compliance operations.`,
        lineItems: [
          { label: 'Total compliance spend', amount: complianceSpend, note: `${countryCount} countries × vendor + internal cost` },
          { label: 'Reconciliation labor', amount: reconLabor, note: 'Monthly tax-related reconciliation effort' },
          { label: 'Audit preparation', amount: auditPrep, note: 'Readiness, documentation, response capacity' },
          { label: 'Error correction & restatement', amount: errorCorrection, note: 'Data quality issues, corrections, amendments' },
        ],
        total,
        taxeraTotal: Math.round(total * 0.42),
        savingsNote: 'Automated reconciliation, instant audit readiness, single source of truth',
      };
    },
  },
};

/* ═══════════════════════════════════════════
   SHARED UTILITIES
   ═══════════════════════════════════════════ */

const TONE_COLORS = { good: '#10b981', warn: '#f59e0b', bad: '#f97316', critical: '#ef4444' };
const SCORE_LABELS = [
  { min: 80, label: 'Strong', color: '#10b981' },
  { min: 60, label: 'Fair', color: '#84cc16' },
  { min: 40, label: 'Exposed', color: '#f59e0b' },
  { min: 20, label: 'At Risk', color: '#f97316' },
  { min: 0, label: 'Critical', color: '#ef4444' },
];
const getScoreLabel = (s) => SCORE_LABELS.find(l => s >= l.min) || SCORE_LABELS[4];

const AnimCounter = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    const start = ref.current;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (value - start) * eased));
      if (p < 1) requestAnimationFrame(tick);
      else ref.current = value;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{display}</>;
};

/* ═══════════════════════════════════════════
   PERSONA HUB — Entry point (redesigned)
   ═══════════════════════════════════════════ */

const PersonaHub = ({ onSelect }) => (
  <div data-testid="persona-hub">
    {/* Clean dark hero — minimal decoration */}
    <div className="relative bg-slate-900 overflow-hidden -mt-4">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-taxera-teal/[0.04] rounded-full blur-[140px]" />
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-20 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-5">
            See it through{' '}
            <span className="taxera-gradient-text">your lens.</span>
          </h1>
          <p className="text-base lg:text-lg text-slate-400 leading-relaxed max-w-xl mx-auto">
            Choose your perspective. Get a score, cost analysis, and transformation roadmap in under 3 minutes.
          </p>
        </motion.div>

        {/* Persona Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Object.values(PERSONAS).map((persona, i) => {
            const Icon = persona.icon;
            return (
              <motion.button
                key={persona.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                onClick={() => onSelect(persona.id)}
                className="group relative text-left rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300 hover:-translate-y-1 p-7"
                data-testid={`persona-card-${persona.id}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${persona.color}15` }}
                  >
                    <Icon className="w-6 h-6" strokeWidth={1.5} style={{ color: persona.color }} />
                  </div>
                </div>

                <h3 className="font-heading text-lg font-bold text-white mb-1">{persona.title}</h3>
                <div className="text-2xs font-bold uppercase tracking-[0.15em] mb-3" style={{ color: persona.color }}>
                  {persona.subtitle}
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mb-6 min-h-[2.5rem]">
                  {persona.description}
                </p>

                <div className="flex items-center gap-2 text-sm font-semibold transition-colors duration-200" style={{ color: persona.color }}>
                  <span>Start assessment</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </motion.button>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-xs text-slate-500 mt-8 flex items-center justify-center gap-2"
        >
          <Lock className="w-3 h-3" />
          Used by enterprise tax teams at Amadeus, KUKA, Voestalpine, and more
        </motion.p>
      </div>
    </div>

    {/* Business Case & Benchmarks — streamlined */}
    <div className="bg-white">
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link
            to="/evaluate/business-case"
            className="group flex items-start gap-5 p-6 rounded-2xl border border-slate-200 hover:border-taxera-blue/30 hover:shadow-md transition-all"
            data-testid="business-case-cta"
          >
            <div className="w-11 h-11 rounded-xl bg-taxera-blue/8 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-taxera-blue" />
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors mb-1">Business Case Builder</h3>
              <p className="text-sm text-slate-500 leading-relaxed">Select products, input your numbers, generate a shareable ROI case.</p>
            </div>
          </Link>

          <Link
            to="/evaluate/benchmarks"
            className="group flex items-start gap-5 p-6 rounded-2xl border border-slate-200 hover:border-amber-400/30 hover:shadow-md transition-all"
            data-testid="benchmarks-cta"
          >
            <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
              <BarChart3 className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal group-hover:text-amber-600 transition-colors mb-1">Industry Benchmarks</h3>
              <p className="text-sm text-slate-500 leading-relaxed">See how your compliance posture compares to peers in your industry.</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   PHASE 0: INTRO
   ═══════════════════════════════════════════ */

const IntroPhase = ({ persona, onStart }) => {
  const Icon = persona.icon;
  return (
    <div className="min-h-[70vh] flex items-center" data-testid="diagnostic-intro">
      <div className="max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-full mb-6" style={{ backgroundColor: `${persona.color}12`, color: persona.color }}>
            <Icon className="w-4 h-4" />
            {persona.intro.badge}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-6">
            {persona.intro.headline[0]} <br />
            <span className="taxera-gradient-text">{persona.intro.headline[1]}</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
            {persona.intro.body}
          </p>
          <button
            onClick={onStart}
            className="group inline-flex items-center gap-3 px-10 py-5 taxera-gradient text-white rounded-full font-semibold text-lg hover:shadow-2xl transition-all hover:scale-105"
            data-testid="start-diagnostic-btn"
          >
            Start Diagnostic
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PHASE 1: FOOTPRINT (shared)
   ═══════════════════════════════════════════ */

const FootprintPhase = ({ selectedRegions, toggleRegion, countryCount, mandateCount, onNext }) => {
  const REGION_DATA = REGIONS.map(r => ({
    ...r,
    count: COUNTRIES.filter(c => c.regionKey === r.key).length,
    examples: COUNTRIES.filter(c => c.regionKey === r.key).slice(0, 4).map(c => c.country),
  }));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8" data-testid="diagnostic-footprint">
      <div className="mb-8">
        <div className="text-xs font-bold tracking-[0.2em] text-taxera-teal uppercase mb-2">Step 1 of 4</div>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2">Where do you operate?</h2>
        <p className="text-slate-500">Select all regions where you have tax obligations</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {REGION_DATA.map((r, i) => {
          const isActive = selectedRegions.has(r.key);
          return (
            <motion.button
              key={r.key}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              onClick={() => toggleRegion(r.key)}
              className={`relative text-left p-6 rounded-2xl transition-all duration-300 overflow-hidden ${
                isActive ? 'bg-slate-900 shadow-xl scale-[1.02]' : 'bg-white border border-slate-200 hover:shadow-md hover:border-slate-300'
              }`}
              data-testid={`region-btn-${r.key}`}
            >
              <div className="absolute top-0 left-0 w-full h-1 transition-opacity duration-300" style={{ backgroundColor: r.color, opacity: isActive ? 1 : 0.3 }} />
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe className={`w-4 h-4 ${isActive ? 'text-white/60' : 'text-slate-400'}`} strokeWidth={1.5} />
                  <span className={`font-heading text-base font-bold ${isActive ? 'text-white' : 'text-taxera-charcoal'}`}>{r.label}</span>
                </div>
                <span className="font-heading text-2xl font-bold" style={{ color: r.color }}>{r.count}</span>
              </div>
              <p className={`text-xs leading-relaxed ${isActive ? 'text-slate-400' : 'text-slate-500'}`}>{r.examples.join(', ')}...</p>
              {isActive && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-taxera-teal flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {countryCount > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <div className="font-heading text-3xl font-bold text-taxera-charcoal">{countryCount}</div>
                  <div className="text-xs text-slate-500">countries</div>
                </div>
                <div className="w-px h-10 bg-slate-200" />
                <div>
                  <div className="font-heading text-3xl font-bold text-taxera-teal">{mandateCount}</div>
                  <div className="text-xs text-slate-500">mandates detected</div>
                </div>
              </div>
              {countryCount >= 3 ? (
                <button onClick={onNext} className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105 shrink-0" data-testid="footprint-next-btn">
                  Next: Diagnostic
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <span className="text-sm text-slate-400 shrink-0">Select at least 3 countries</span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   PHASE 2: PAIN DIAGNOSTIC (parameterized)
   ═══════════════════════════════════════════ */

const DiagnosticPhase = ({ persona, answers, setAnswer, onNext }) => {
  const dims = persona.painDimensions;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === dims.length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8" data-testid="diagnostic-pain">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] text-taxera-teal uppercase mb-2">Step 2 of 4</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2">Where does it hurt?</h2>
          <p className="text-slate-500">One click per row. Be honest — the value is in the truth.</p>
        </div>
        <button
          onClick={allAnswered ? onNext : undefined}
          disabled={!allAnswered}
          className={`shrink-0 group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all ${
            allAnswered ? 'taxera-gradient text-white hover:shadow-lg hover:scale-105 cursor-pointer' : 'bg-slate-100 text-slate-300 cursor-not-allowed'
          }`}
          data-testid="see-score-btn"
        >
          See my score
          {allAnswered ? <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /> : <span className="text-xs opacity-60">{answeredCount}/{dims.length}</span>}
        </button>
      </div>

      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-8">
        <motion.div className="h-full taxera-gradient rounded-full" animate={{ width: `${(answeredCount / dims.length) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>

      <div className="space-y-4">
        {dims.map((dim, dimIdx) => {
          const Icon = dim.icon;
          const selected = answers[dim.id];
          const hasAnswer = selected !== undefined;

          return (
            <motion.div
              key={dim.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: dimIdx * 0.06 }}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${hasAnswer ? 'border-slate-200 bg-white shadow-sm' : 'border-slate-200 bg-white'}`}
              data-testid={`pain-dim-${dim.id}`}
            >
              <div className="h-1" style={{ backgroundColor: dim.accent, opacity: hasAnswer ? 1 : 0.25 }} />
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: hasAnswer ? dim.accent : `${dim.accent}15` }}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: hasAnswer ? '#fff' : dim.accent }} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-taxera-charcoal">{dim.label}</h3>
                    <p className="text-sm text-slate-500">{dim.question}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {dim.options.map((opt, optIdx) => {
                    const isSelected = selected === optIdx;
                    return (
                      <button
                        key={opt.label}
                        onClick={() => setAnswer(dim.id, optIdx)}
                        className={`px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 text-center border ${
                          isSelected ? 'text-white shadow-lg scale-[1.03] border-transparent' : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                        }`}
                        style={isSelected ? { backgroundColor: TONE_COLORS[opt.tone] } : {}}
                        data-testid={`pain-opt-${dim.id}-${optIdx}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <AnimatePresence>
                  {hasAnswer && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="text-sm text-slate-500 italic mt-3 pt-3 border-t border-slate-100">
                      {dim.insights[selected]}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {allAnswered && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-center md:hidden">
            <button onClick={onNext} className="group inline-flex items-center gap-2 px-10 py-5 taxera-gradient text-white rounded-full font-semibold text-lg hover:shadow-2xl transition-all hover:scale-105">
              See my score <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   PHASE 3: SCORE REVEAL (parameterized)
   ═══════════════════════════════════════════ */

const RevealPhase = ({ persona, answers, countryCount, onNext }) => {
  const [revealed, setRevealed] = useState(false);
  useEffect(() => { setTimeout(() => setRevealed(true), 400); }, []);

  const dims = persona.painDimensions;
  const dimensionScores = dims.map((dim) => {
    const optIdx = answers[dim.id];
    return { ...dim, score: dim.options[optIdx]?.score ?? 0, optIdx, insight: dim.insights[optIdx] };
  });
  const totalScore = dimensionScores.reduce((s, d) => s + d.score, 0);
  const scoreInfo = getScoreLabel(totalScore);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8" data-testid="diagnostic-reveal">
      <div className="bg-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(20,163,184,0.12)_0%,transparent_60%)]" />
        <div className="relative z-10">
          <div className="text-xs font-bold tracking-[0.2em] text-taxera-teal uppercase mb-6">Step 3 of 4</div>

          <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start mb-8">
            <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={revealed ? { scale: 1, opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-center md:text-left">
              <div className="text-sm text-slate-400 mb-1">Your {persona.title} Score</div>
              <div className="font-heading text-7xl lg:text-8xl font-bold leading-none" style={{ color: scoreInfo.color }}>
                {revealed ? <AnimCounter value={totalScore} duration={1200} /> : 0}
              </div>
              <div className="flex items-center gap-2 mt-2 justify-center md:justify-start">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: scoreInfo.color }} />
                <span className="text-lg font-bold" style={{ color: scoreInfo.color }}>{scoreInfo.label}</span>
                <span className="text-sm text-slate-500">/ 100</span>
              </div>
            </motion.div>

            <div className="space-y-3">
              {dimensionScores.map((dim, i) => {
                const Icon = dim.icon;
                const barColor = getScoreLabel(dim.score * 5).color;
                return (
                  <motion.div
                    key={dim.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={revealed ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    className="flex items-center gap-3"
                    data-testid={`score-bar-${dim.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" style={{ color: dim.accent }} strokeWidth={1.5} />
                    <span className="text-xs font-semibold text-slate-400 w-36 shrink-0">{dim.label}</span>
                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className="h-full rounded-full" style={{ backgroundColor: barColor }} initial={{ width: '0%' }} animate={revealed ? { width: `${(dim.score / 20) * 100}%` } : {}} transition={{ duration: 0.7, delay: 0.4 + i * 0.08 }} />
                    </div>
                    <span className="text-xs font-bold w-10 text-right" style={{ color: barColor }}>{dim.score}/20</span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={revealed ? { opacity: 1 } : {}} transition={{ delay: 1 }} className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {dimensionScores.map((dim) => (
              <div key={dim.id} className="bg-white/5 rounded-xl p-3">
                <div className="text-2xs font-bold uppercase tracking-wide mb-1" style={{ color: dim.accent }}>{dim.label}</div>
                <p className="text-xs text-slate-500 leading-relaxed">{dim.insight}</p>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={revealed ? { opacity: 1, y: 0 } : {}} transition={{ delay: 1.2 }} className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/10">
            <p className="text-sm text-slate-400">
              <span className="text-white font-bold">{countryCount} countries</span> analyzed.
              {totalScore < 60 && ' That level of exposure is costing more than you think.'}
              {totalScore >= 60 && totalScore < 80 && ' Solid base — but there\'s significant room for improvement.'}
              {totalScore >= 80 && ' Impressive. Let\'s make it bulletproof.'}
            </p>
            <button onClick={onNext} className="group shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-charcoal rounded-full font-semibold hover:shadow-2xl transition-all hover:scale-105" data-testid="see-cost-impact-btn">
              What is this costing you? <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   PHASE 4: COST IMPACT
   ═══════════════════════════════════════════ */

const formatCurrency = (n) => {
  if (n >= 1000000) return `€${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `€${Math.round(n / 1000)}K`;
  return `€${Math.round(n)}`;
};

const CostImpactPhase = ({ persona, answers, countryCount, mandateCount, onNext, onSave }) => {
  const [revealed, setRevealed] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setTimeout(() => setRevealed(true), 300); }, []);

  const cost = persona.costModel(answers, countryCount, mandateCount);
  const savings = cost.total - cost.taxeraTotal;
  const savingsPct = Math.round((savings / cost.total) * 100);

  const handleNext = () => {
    // Auto-save when moving to transformation
    if (onSave && !saved) {
      onSave(cost);
      setSaved(true);
    }
    onNext();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8" data-testid="diagnostic-cost-impact">
      <div className="mb-8">
        <div className="text-xs font-bold tracking-[0.2em] text-taxera-teal uppercase mb-2">Step 4 of 4</div>
        <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2">
          {cost.headline}
        </h2>
        <p className="text-slate-500">{cost.subline}</p>
      </div>

      {/* Current cost breakdown */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <h3 className="font-heading text-sm font-bold text-slate-500 uppercase tracking-wide">Your estimated annual cost — today</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {cost.lineItems.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -15 }}
              animate={revealed ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.1 }}
              className="flex items-center justify-between px-6 py-4 group"
              data-testid={`cost-item-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-taxera-charcoal">{item.label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{item.note}</div>
              </div>
              <div className="font-heading text-lg font-bold text-red-500 shrink-0 ml-4" data-testid={`cost-amount-${i}`}>
                {revealed ? formatCurrency(item.amount) : '—'}
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={revealed ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="px-6 py-5 bg-red-50/60 border-t border-red-100 flex items-center justify-between"
        >
          <span className="font-heading text-base font-bold text-taxera-charcoal">Estimated annual total</span>
          <span className="font-heading text-2xl lg:text-3xl font-bold text-red-600" data-testid="cost-total-current">
            {revealed ? formatCurrency(cost.total) : '—'}
          </span>
        </motion.div>
      </div>

      {/* With Taxera */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={revealed ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.8 }}
        className="bg-slate-900 rounded-2xl overflow-hidden mb-6"
      >
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-sm font-bold text-taxera-teal uppercase tracking-wide">With the Taxera Platform</h3>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">Projected annual cost</div>
              <div className="font-heading text-3xl font-bold text-white" data-testid="cost-total-taxera">
                {revealed ? formatCurrency(cost.taxeraTotal) : '—'}
              </div>
              <div className="text-xs text-slate-500 mt-1">{cost.savingsNote}</div>
            </div>

            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">Annual savings</div>
              <div className="font-heading text-4xl lg:text-5xl font-bold text-emerald-400" data-testid="cost-savings">
                {revealed ? <AnimCounter value={savings} duration={1000} /> : 0}
              </div>
              <div className="text-xs text-emerald-400 font-semibold mt-1">
                {revealed ? `€ saved — ${savingsPct}% reduction` : '—'}
              </div>
            </div>

            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">Payback period</div>
              <div className="font-heading text-3xl font-bold text-white">
                {savings > 200000 ? '< 6' : savings > 100000 ? '< 12' : '< 18'}
              </div>
              <div className="text-xs text-slate-500 mt-1">months (estimated)</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Cost comparison bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={revealed ? { opacity: 1 } : {}}
        transition={{ delay: 1.1 }}
        className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mb-8"
      >
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs font-bold text-slate-400 w-16 shrink-0">Today</span>
          <div className="flex-1 h-4 bg-red-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-red-400 rounded-full"
              initial={{ width: '0%' }}
              animate={revealed ? { width: '100%' } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            />
          </div>
          <span className="text-sm font-bold text-red-600 w-20 text-right">{formatCurrency(cost.total)}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs font-bold text-slate-400 w-16 shrink-0">Taxera</span>
          <div className="flex-1 h-4 bg-emerald-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-emerald-400 rounded-full"
              initial={{ width: '0%' }}
              animate={revealed ? { width: `${(cost.taxeraTotal / cost.total) * 100}%` } : {}}
              transition={{ duration: 0.8, delay: 1.4 }}
            />
          </div>
          <span className="text-sm font-bold text-emerald-600 w-20 text-right">{formatCurrency(cost.taxeraTotal)}</span>
        </div>
        <p className="text-2xs text-slate-400 mt-3 italic">
          Figures are illustrative estimates based on industry benchmarks and your diagnostic inputs. Actual costs vary by organization.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={revealed ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1.5 }}
        className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-2xl border border-slate-200 p-6"
      >
        <p className="text-sm text-slate-600">
          <span className="font-bold text-taxera-charcoal">{formatCurrency(savings)}/year</span> in potential savings.
          Get your personalized breakdown and transformation plan.
        </p>
        <button
          onClick={handleNext}
          className="group shrink-0 inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-xl transition-all hover:scale-105"
          data-testid="see-transformation-btn"
        >
          Get my report
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   PHASE 5: TRANSFORMATION + GATE
   ═══════════════════════════════════════════ */

const TransformationPhase = ({ persona, answers, countryCount, mandateCount }) => {
  const [form, setForm] = useState({ name: '', company: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const dims = persona.painDimensions;
  const totalScore = dims.reduce((s, d) => s + (d.options[answers[d.id]]?.score ?? 0), 0);
  const metrics = persona.transformMetrics(totalScore, countryCount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.company || !form.email) { setError('All fields are required'); return; }
    const freeProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com'];
    const domain = form.email.split('@')[1]?.toLowerCase();
    if (freeProviders.includes(domain)) { setError('Please use your company email address'); return; }
    setSubmitting(true);
    try {
      const painScores = {};
      dims.forEach(d => { painScores[d.id] = d.options[answers[d.id]]?.score ?? 0; });
      await fetch(`${API}/api/leads/diagnostic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, persona: persona.id, countryCount, mandateCount, painScores, healthScore: totalScore }),
      });
      setSubmitted(true);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-8" data-testid="diagnostic-transformation">
      {!submitted ? (
        <>
          <div className="mb-8">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2">What changes with Taxera</h2>
            <p className="text-slate-500">Based on your {persona.title.toLowerCase()} diagnostic results</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {metrics.map((m, i) => (
              <motion.div key={m.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-4">{m.label}</div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xs text-slate-400 mb-0.5">Today</div>
                    <div className="font-heading text-xl font-bold" style={{ color: m.beforeColor }}>{m.before}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <div>
                    <div className="text-2xs text-taxera-teal mb-0.5">Taxera</div>
                    <div className="font-heading text-xl font-bold" style={{ color: m.afterColor }}>{m.after}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(20,163,184,0.1)_0%,transparent_50%)]" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-taxera-teal/15 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-taxera-teal" />
                </div>
                <div>
                  <span className="text-xs font-bold tracking-[0.15em] text-taxera-teal uppercase block">Your report is ready</span>
                  <span className="text-sm text-slate-400">Score: {totalScore}/100 across {countryCount} countries</span>
                </div>
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mt-4 mb-1">Get your {persona.reportTitle}</h3>
              <p className="text-sm text-slate-400 mb-6">{persona.reportDesc} A Taxera specialist will walk you through the findings.</p>
              <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-4">
                <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-taxera-teal" data-testid="diag-name-input" />
                <input type="text" value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company" className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-taxera-teal" data-testid="diag-company-input" />
                <input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Company email" className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-taxera-teal" data-testid="diag-email-input" />
                <div className="md:col-span-3">
                  {error && <p className="text-red-400 text-sm mb-3" data-testid="diag-error">{error}</p>}
                  <button type="submit" disabled={submitting} className="group w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all disabled:opacity-60" data-testid="diag-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? 'Submitting...' : 'Send me my report'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      ) : (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16" data-testid="diag-success">
          <div className="w-16 h-16 bg-taxera-teal/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-taxera-teal" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">Report requested</h2>
          <p className="text-base text-slate-600 mb-1">Your personalized {persona.reportTitle} is being prepared.</p>
          <p className="text-sm text-slate-500">A Taxera specialist will walk you through the findings within 24 hours.</p>
        </motion.div>
      )}
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   MAIN PAGE ORCHESTRATOR
   ═══════════════════════════════════════════ */

const EvaluatePage = () => {
  const { user } = useAuth();
  const [personaId, setPersonaId] = useState(null);
  const [phase, setPhase] = useState(0);
  const [selectedRegions, setSelectedRegions] = useState(new Set());
  const [painAnswers, setPainAnswers] = useState({});

  const persona = personaId ? PERSONAS[personaId] : null;

  const selectedCountries = useMemo(() =>
    COUNTRIES.filter(c => selectedRegions.has(c.regionKey)),
  [selectedRegions]);

  const detectedMandates = useMemo(() => {
    if (!selectedCountries.length) return [];
    return SOLUTIONS
      .map(s => ({ ...s, countryCount: selectedCountries.filter(c => c[s.key] === 'yes').length }))
      .filter(s => s.countryCount > 0)
      .sort((a, b) => b.countryCount - a.countryCount);
  }, [selectedCountries]);

  const toggleRegion = (key) => {
    setSelectedRegions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveDiagnostic = useCallback(async (costData) => {
    if (!user || !persona) return;
    const painScores = {};
    persona.painDimensions.forEach(d => {
      painScores[d.id] = d.options[painAnswers[d.id]]?.score ?? 0;
    });
    const totalScore = Object.values(painScores).reduce((s, v) => s + v, 0);
    try {
      await fetch(`${API}/api/diagnostics/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          persona: persona.id,
          countryCount: selectedCountries.length,
          mandateCount: detectedMandates.length,
          selectedRegions: [...selectedRegions],
          painAnswers,
          painScores,
          healthScore: totalScore,
          costData: { total: costData.total, taxeraTotal: costData.taxeraTotal, savings: costData.total - costData.taxeraTotal },
        }),
      });
    } catch (e) { console.error('Failed to save diagnostic:', e); }
  }, [user, persona, painAnswers, selectedCountries, detectedMandates, selectedRegions]);

  const restart = () => {
    setPersonaId(null);
    setPhase(0);
    setSelectedRegions(new Set());
    setPainAnswers({});
  };

  const backToHub = () => {
    setPersonaId(null);
    setPhase(0);
    setSelectedRegions(new Set());
    setPainAnswers({});
  };

  return (
    <PageLayout>
      <AnimatePresence mode="wait">
        {!personaId && (
          <PersonaHub key="hub" onSelect={(id) => { setPersonaId(id); setPhase(0); }} />
        )}
      </AnimatePresence>

      {personaId && (
        <div className="max-w-5xl mx-auto px-6">
          {/* Persistent progress bar */}
          {phase >= 1 && phase <= 5 && (
            <div className="pt-6 pb-2" data-testid="diagnostic-progress-bar">
              <div className="flex items-center gap-3">
                {['Footprint', 'Diagnostic', 'Score', 'Cost Impact', 'Report'].map((label, i) => {
                  const stepNum = i + 1;
                  const isActive = phase === stepNum;
                  const isDone = phase > stepNum;
                  return (
                    <div key={label} className="flex items-center gap-3 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold transition-colors ${
                          isDone ? 'bg-taxera-teal text-white' : isActive ? 'bg-taxera-blue text-white' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : stepNum}
                        </div>
                        <span className={`text-xs font-medium truncate hidden sm:block ${isActive ? 'text-taxera-charcoal' : 'text-slate-400'}`}>{label}</span>
                      </div>
                      {i < 4 && <div className={`flex-1 h-px ${isDone ? 'bg-taxera-teal' : 'bg-slate-200'}`} />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Back / Restart controls */}
          <div className="pt-4 flex items-center justify-between">
            <button onClick={backToHub} className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1.5" data-testid="back-to-hub-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> All personas
            </button>
            {phase > 0 && (
              <button onClick={restart} className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1.5" data-testid="restart-diagnostic">
                <RotateCcw className="w-3.5 h-3.5" /> Start over
              </button>
            )}
          </div>

          <AnimatePresence mode="wait">
            {phase === 0 && (
              <IntroPhase key="intro" persona={persona} onStart={() => setPhase(1)} />
            )}

            {phase === 1 && (
              <FootprintPhase key="footprint"
                selectedRegions={selectedRegions} toggleRegion={toggleRegion}
                countryCount={selectedCountries.length} mandateCount={detectedMandates.length}
                onNext={() => setPhase(2)} />
            )}

            {phase === 2 && (
              <DiagnosticPhase key="diag" persona={persona}
                answers={painAnswers} setAnswer={(id, idx) => setPainAnswers(p => ({ ...p, [id]: idx }))}
                onNext={() => setPhase(3)} />
            )}

            {phase === 3 && (
              <RevealPhase key="reveal" persona={persona} answers={painAnswers}
                countryCount={selectedCountries.length} onNext={() => setPhase(4)} />
            )}

            {phase === 4 && (
              <CostImpactPhase key="cost" persona={persona} answers={painAnswers}
                countryCount={selectedCountries.length} mandateCount={detectedMandates.length}
                onNext={() => setPhase(5)} onSave={saveDiagnostic} />
            )}

            {phase === 5 && (
              <TransformationPhase key="transform" persona={persona} answers={painAnswers}
                countryCount={selectedCountries.length} mandateCount={detectedMandates.length} />
            )}
          </AnimatePresence>
        </div>
      )}
    </PageLayout>
  );
};

export default EvaluatePage;
